import { Routes, Route } from 'react-router-dom';
import { SystemRole } from '@peles/shared';
import { HomePage } from '../pages/HomePage';
import { LoginPage } from '../pages/LoginPage';
import { GoogleCallbackPage } from '../pages/GoogleCallbackPage';
import { UnauthorizedPage } from '../pages/UnauthorizedPage';
import { UsersPage } from '../pages/UsersPage';
import { EventsPage } from '../pages/EventsPage';
import { EventDetailPage } from '../pages/EventDetailPage';
import { VenueDetailPage } from '../pages/VenueDetailPage';
import { VendorDetailPage } from '../pages/VendorDetailPage';
import { TemplatesPage } from '../pages/TemplatesPage';
import { TimelineTemplateEditPage } from '../pages/TimelineTemplateEditPage';
import { TimelineEditPage } from '../pages/TimelineEditPage';
import { VenuesPage } from '../pages/VenuesPage';
import { VendorsPage } from '../pages/VendorsPage';
import { ExpensesPage } from '../pages/ExpensesPage';
import { TimelinePage } from '../pages/TimelinePage';
import { TaskListPage } from '../pages/TaskListPage';
import { KibuddimPage } from '../pages/KibuddimPage';
import { GuestListPage } from '../pages/GuestListPage';
import { FloorPlanPage } from '../pages/FloorPlanPage';
import { PhotosPage } from '../pages/PhotosPage';
import { ProfilePage } from '../pages/ProfilePage';
import { AdminSqlClientPage } from '../pages/AdminSqlClientPage';
import { SettingsPage } from '../pages/SettingsPage';
import { ClientDashboardPage } from '../pages/ClientDashboardPage';
import { ClientEventDetailPage } from '../pages/ClientEventDetailPage';
import { VendorDashboardPage } from '../pages/VendorDashboardPage';
import { InvitationsPage } from '../pages/InvitationsPage';
import { ReportsPage } from '../pages/ReportsPage';
import { EmailLogsPage } from '../pages/EmailLogsPage';
import { AuditLogsPage } from '../pages/AuditLogsPage';
import { BackupsPage } from '../pages/BackupsPage';
import { DataImportPage } from '../pages/DataImportPage';
import { CalendarPage } from '../pages/CalendarPage';
import { InquiriesPage } from '../pages/InquiriesPage';
import { lazy, Suspense } from 'react';
import { FilesPage } from '../pages/FilesPage';
import { NotificationsPage } from '../pages/NotificationsPage';
import { CommunicationsPage } from '../pages/CommunicationsPage';
import { QuestionnairesPage } from '../pages/QuestionnairesPage';
import { QuestionnaireDetailPage } from '../pages/QuestionnaireDetailPage';
import { PublicQuestionnairePage } from '../pages/PublicQuestionnairePage';
import { PublicRsvpPage } from '../pages/PublicRsvpPage';
import { SeatFinderPage } from '../pages/SeatFinderPage';
import { PublicVendorUploadPage } from '../pages/PublicVendorUploadPage';
import { CheckInPage } from '../pages/CheckInPage';
import { CommissionsPage } from '../pages/CommissionsPage';
import { ProtectedRoute } from '../components/common/ProtectedRoute';
import { AppLayout } from '../components/layout';
import { isPublicSite } from '../utils/is-public-site';
import { InviteShareRedirect } from '../components/invites/InviteShareRedirect';

export { isPublicSite };

/**
 * The public marketing site is its own chunk. It shares nothing with the app
 * beyond React and the router, and keeping it out of the main bundle keeps
 * that bundle under the service worker's precache limit — the app never pays
 * for the marketing pages and the marketing pages never pay for the app.
 */
const PublicLayout = lazy(() =>
  import('../components/layout/PublicLayout').then((m) => ({ default: m.PublicLayout })),
);
const LandingPage = lazy(() =>
  import('../pages/LandingPage').then((m) => ({ default: m.LandingPage })),
);
const AboutPage = lazy(() => import('../pages/AboutPage').then((m) => ({ default: m.AboutPage })));
const GalleryPage = lazy(() =>
  import('../pages/GalleryPage').then((m) => ({ default: m.GalleryPage })),
);

/** Ivory ground while a public chunk loads, so the page never flashes white. */
function PublicFallback() {
  return <div className="min-h-screen bg-brand-ground" aria-busy="true" />;
}

export function AppRouter() {
  if (isPublicSite()) {
    return (
      <Routes>
        <Route path="/questionnaire/:token" element={<PublicQuestionnairePage />} />
        <Route path="/rsvp/:token" element={<PublicRsvpPage />} />
        <Route path="/rsvp/:token/seat" element={<SeatFinderPage />} />
        <Route path="/i/:token" element={<InviteShareRedirect />} />
        <Route path="/vendor-upload/:token" element={<PublicVendorUploadPage />} />
        <Route
          element={
            <Suspense fallback={<PublicFallback />}>
              <PublicLayout />
            </Suspense>
          }
        >
          <Route index element={<LandingPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/gallery" element={<GalleryPage />} />
          <Route path="*" element={<LandingPage />} />
        </Route>
      </Routes>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/google/callback" element={<GoogleCallbackPage />} />
      <Route path="/unauthorized" element={<UnauthorizedPage />} />
      <Route path="/questionnaire/:token" element={<PublicQuestionnairePage />} />
      <Route path="/rsvp/:token" element={<PublicRsvpPage />} />
      <Route path="/rsvp/:token/seat" element={<SeatFinderPage />} />
      <Route path="/i/:token" element={<InviteShareRedirect />} />
      <Route path="/vendor-upload/:token" element={<PublicVendorUploadPage />} />

      {/* Protected routes with layout */}
      <Route
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<HomePage />} />

        {/* Client Portal */}
        <Route
          path="client"
          element={
            <ProtectedRoute minimumRole={SystemRole.CLIENT}>
              <ClientDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="client/events/:eventId"
          element={
            <ProtectedRoute minimumRole={SystemRole.CLIENT}>
              <ClientEventDetailPage />
            </ProtectedRoute>
          }
        />

        {/* Vendor Portal */}
        <Route
          path="vendor"
          element={
            <ProtectedRoute minimumRole={SystemRole.VENDOR}>
              <VendorDashboardPage />
            </ProtectedRoute>
          }
        />

        {/* Invitations (PLANNER+) */}
        <Route
          path="invitations"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <InvitationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="events/:eventId/invitations"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <InvitationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="events/:eventId/checkin"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <CheckInPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="users"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <UsersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="calendar"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <CalendarPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="events"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <EventsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="events/:eventId"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <EventDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="venues"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <VenuesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="venues/:venueId"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <VenueDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="vendors"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <VendorsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="vendors/:vendorId"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <VendorDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="expenses"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <ExpensesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="commissions"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <CommissionsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="events/:eventId/expenses"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <ExpensesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="timelines"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <TimelinePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="events/:eventId/timeline"
          element={
            <ProtectedRoute minimumRole={SystemRole.CLIENT}>
              <TimelinePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="events/:eventId/timeline/:timelineId"
          element={
            <ProtectedRoute minimumRole={SystemRole.CLIENT}>
              <TimelineEditPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="checklists"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <TaskListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="events/:eventId/checklist"
          element={
            <ProtectedRoute minimumRole={SystemRole.CLIENT}>
              <TaskListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="kibuddim"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <KibuddimPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="events/:eventId/kibuddim"
          element={
            <ProtectedRoute minimumRole={SystemRole.CLIENT}>
              <KibuddimPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="guests"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <GuestListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="events/:eventId/guests"
          element={
            <ProtectedRoute minimumRole={SystemRole.CLIENT}>
              <GuestListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="floor-plans"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <FloorPlanPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="events/:eventId/floor-plan"
          element={
            <ProtectedRoute minimumRole={SystemRole.CLIENT}>
              <FloorPlanPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="photos"
          element={
            <ProtectedRoute minimumRole={SystemRole.CLIENT}>
              <PhotosPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="events/:eventId/questionnaires"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <QuestionnairesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="events/:eventId/questionnaires/:questionnaireId"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <QuestionnaireDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="templates"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <TemplatesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="templates/questionnaire/:questionnaireId"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <QuestionnaireDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="templates/timeline/:templateId"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <TimelineTemplateEditPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="settings"
          element={
            <ProtectedRoute minimumRole={SystemRole.ADMIN}>
              <SettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="reports"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <ReportsPage />
            </ProtectedRoute>
          }
        />
        {/*
          No minimum role: everybody who signs in has notifications, and what
          reaches them is decided per recipient rather than per role.
        */}
        <Route path="notifications" element={<NotificationsPage />} />
        {/*
          PLANNER+, because a client's own conversations reach them through the
          portal and this lists correspondence across every record they are on.
          Widening it is H5's decision, not this route's.
        */}
        <Route
          path="communications"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <CommunicationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="files"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <FilesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="import"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <DataImportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/email-logs"
          element={
            <ProtectedRoute minimumRole={SystemRole.ADMIN}>
              <EmailLogsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/audit-logs"
          element={
            <ProtectedRoute minimumRole={SystemRole.ADMIN}>
              <AuditLogsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/backups"
          element={
            <ProtectedRoute minimumRole={SystemRole.ADMIN}>
              <BackupsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/inquiries"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <InquiriesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/sql-client"
          element={
            <ProtectedRoute minimumRole={SystemRole.ADMIN}>
              <AdminSqlClientPage />
            </ProtectedRoute>
          }
        />
        <Route path="profile" element={<ProfilePage />} />
      </Route>
    </Routes>
  );
}
